import {
  BillingAcceptanceAdvancedQuery,
  BillingAcceptanceAdvancedQueryVariables,
  BillingAcceptanceAdvancedDocument,
  AcceptanceAdvanceStatus,
} from 'domain/api/graphql/generated';

import { acceptanceAdvancedStatusUpdated, stopAcceptanceAdvancedProcess } from './slice';
import { AcceptanceAdvancedStopPollingArgs, PollAcceptanceAdvancedStatusArgs } from './types';
import { TEXTS } from './texts';
import { TEXTS as GLOBAL_POLLING_TEXTS } from '../globalPollingSlice/texts';
import { DEFAULT_MAX_ATTEMPTS, DEFAULT_POLLING_INTERVAL, DEFAULT_STOP_POLLING_TIMEOUT } from './consts';
import { startGlobalPolling, stopGlobalPolling } from '../globalPollingSlice';
import { ActionPollingConfig } from '../globalPollingSlice/types';

const stopPollingByReason = ({
  acceptanceAdvancedId,
  dispatch,
  reason,
}: Omit<AcceptanceAdvancedStopPollingArgs, 'isTabClosed' | 'acceptanceAdvancedTab'>) => {
  dispatch(stopAcceptanceAdvancedProcess({ acceptanceAdvancedId, clearData: true }));
  dispatch(stopGlobalPolling({ id: acceptanceAdvancedId, reason }));
};

const stopPullingWithCloseTab = ({
  acceptanceAdvancedId,
  dispatch,
  isTabClosed,
  reason,
}: AcceptanceAdvancedStopPollingArgs) => {
  if (!isTabClosed) {
    stopPollingByReason({ acceptanceAdvancedId, dispatch, reason });
  }
};

export const pollAcceptanceAdvancedStatus = async ({
  acceptanceAdvancedId,
  acceptanceAdvancedTab,
  companyId,
  dispatch,
  signal,
}: PollAcceptanceAdvancedStatusArgs): Promise<void> => {
  const checkTabStatus = (tab: Window | null) => {
    if (tab?.closed) {
      console.log(`📭 Tab closed for ID: ${acceptanceAdvancedId}`);

      stopPollingByReason({
        acceptanceAdvancedId,
        dispatch,
        reason: TEXTS.ACCEPTANCE_ADVANCED_TAB_CLOSED,
      });

      return true;
    }

    return false;
  };

  if (checkTabStatus(acceptanceAdvancedTab)) return;

  try {
    const actionConfig: ActionPollingConfig<BillingAcceptanceAdvancedQuery, BillingAcceptanceAdvancedQueryVariables> = {
      query: BillingAcceptanceAdvancedDocument,
      variables: { companyId },
      isComplete: (data) => {
        const status = data?.acceptanceAdvancedQuery?.status ?? null;

        return status !== AcceptanceAdvanceStatus.Pending;
      },
      onComplete: (data) => {
        const status = data?.acceptanceAdvancedQuery?.status ?? null;
        console.log(`🎉 Global Polling COMPLETE! Final status: ${status} for ID: ${acceptanceAdvancedId}`);

        setTimeout(() => {
          dispatch(stopAcceptanceAdvancedProcess({ acceptanceAdvancedId, clearData: false }));
        }, 100);
      },
      onError: (error) => {
        if (checkTabStatus(acceptanceAdvancedTab)) return;

        console.error(`❌ Global Polling error for ID: ${acceptanceAdvancedId}:`, error);

        if (error.message === GLOBAL_POLLING_TEXTS.POLLING_TIMEOUT) {
          stopPullingWithCloseTab({
            acceptanceAdvancedId,
            acceptanceAdvancedTab,
            dispatch,
            isTabClosed: acceptanceAdvancedTab.closed,
            reason: TEXTS.ACCEPTANCE_ADVANCED_TOO_LONG,
          });
        } else if (error.message === GLOBAL_POLLING_TEXTS.POLLING_TOO_MANY_ATTEMPTS) {
          stopPullingWithCloseTab({
            acceptanceAdvancedId,
            acceptanceAdvancedTab,
            dispatch,
            isTabClosed: acceptanceAdvancedTab.closed,
            reason: TEXTS.ACCEPTANCE_ADVANCED_TOO_MANY_ATTEMPTS,
          });
        } else {
          dispatch(
            acceptanceAdvancedStatusUpdated({
              acceptanceAdvancedId,
              error: error.message,
            })
          );
        }
      },
      pollingInterval: DEFAULT_POLLING_INTERVAL,
      maxAttempts: DEFAULT_MAX_ATTEMPTS,
      timeout: DEFAULT_STOP_POLLING_TIMEOUT,
      context: { fetchOptions: { signal } },
    };

    dispatch(
      startGlobalPolling({
        id: acceptanceAdvancedId,
        config: actionConfig,
      })
    );
  } catch (error) {
    if (!signal.aborted) {
      console.error(`🚨 Failed to start global polling for ID: ${acceptanceAdvancedId}:`, error);

      stopPollingByReason({
        acceptanceAdvancedId,
        dispatch,
        reason: 'Failed to start global polling process',
      });
    }
  }
};
